import React from "react";

/**
 * The small set of primitives the Reports screens are built from.
 *
 * The rule they hold: **a boundary is drawn only where there is a real
 * boundary.** A card inside a card is two borders arguing about the same edge,
 * so panels here are one surface with hairline dividers inside them.
 *
 * Almost everything now lives a level up and is re-exported: spacing in
 * `theme/space`, the page band in `components/PageHeader`, and the surfaces
 * (`Panel`, `SectionLabel`, `Divider`, `Stat`, `Field`) in
 * `components/surfaces` — each moved out as a second screen adopted it, and
 * each re-exported so these pages' imports read exactly as they always did.
 * What is left below is what is genuinely a property of *these* pages.
 */
export { space } from "../../theme/space";
export { PageHeader, MetaDot } from "../../components/PageHeader";
export {
  Panel,
  SectionLabel,
  Divider,
  Stat,
  Field,
} from "../../components/surfaces";

/**
 * The reading width — a ceiling, not a target.
 *
 * Without any cap, an ultrawide stretches a three-field form across two feet of
 * glass and parks its Save button a screen away from the thing being saved. But
 * the first number picked here (1440) was set too low: on an ordinary large
 * monitor it left a third of the content area empty while the report *view*
 * beside it ran full width, so the two halves of the same feature disagreed
 * about how wide a page is. Measured on a 2048px viewport, the builder gained
 * real information at 1724 — the status filter stopped collapsing six tags into
 * "+3 …".
 *
 * 1920 is the ceiling because it is where the argument above actually starts to
 * bite; below it the page simply uses what the layout gives it. Anything that
 * genuinely reads worse wide — the roll-up chart band, an empty state's prose —
 * carries its own narrower measure rather than dragging the whole page down.
 */
export const MAX_PAGE_WIDTH = 1920;

/** Wraps a page so every Reports screen shares one measure. */
export const Page: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ maxWidth: MAX_PAGE_WIDTH }}>{children}</div>
);
